import type {
  BreadcrumbList,
  Graph,
  Organization,
  Product,
  Thing,
  VideoObject,
  WithContext,
} from 'schema-dts';
import type { ReviewPage } from '@repo/supergraph/review/base/types';
import type { Video } from '@repo/supergraph-schemas/structuredContent/hydration/WpVideo/types';
import type { BreadcrumbRoot } from '../Breadcrumbs/types';
import { ORGANIZATION } from './constants';
import { getBreadcrumbListSchema, getReviewProductSchema, getVideoSchema } from './utils';

export interface LinkedDataJsonProps<T extends Thing> {
  id: string;
  schema: WithContext<T> | Graph;
}

export function LinkedDataJson<T extends Thing>({ id, schema }: LinkedDataJsonProps<T>) {
  return (
    <script
      id={id}
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
      key={id}
    />
  );
}

export type BreadcrumbListSchema = WithContext<BreadcrumbList>;
export const BreadcrumbListLinkedData = ({
  id,
  breadcrumbs,
}: {
  id: string;
  breadcrumbs: BreadcrumbRoot[];
}) => {
  return <LinkedDataJson<BreadcrumbList> id={id} schema={getBreadcrumbListSchema(breadcrumbs)} />;
};

export const OrganizationLinkedData = ({ id }: { id: string }) => {
  return <LinkedDataJson<Organization> id={id} schema={ORGANIZATION} />;
};

export type VideoSchema = WithContext<VideoObject>;
export const VideoLinkedData = ({ video, id }: { video: Video; id: string }) => {
  if (!video || !video.youtubeVideo) return null;

  return <LinkedDataJson<VideoObject> id={id} schema={getVideoSchema(video)} />;
};

export type ReviewProductSchema = WithContext<Product>;
export default function ReviewLinkedData({ review, id }: { review: ReviewPage; id: string }) {
  return <LinkedDataJson<Product> id={id} schema={getReviewProductSchema(review)} />;
}
